import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  Department,
  Post,
  Tender,
  Facility,
  Event,
  FormSchema,
  FormSubmission,
  Talent,
  Subsite,
  Complaint,
  SecurityClearance,
  PortfolioItem,
  Delegation,
  SiteSettings,
  AnalyticsData,
  PaginatedResponse,
} from '@/types';
import { mockDB } from '@/mocks/seed';
import { generateId, generateCaseNumber } from '@/lib/utils';
import { sendAllNotifications } from './notifications';

// Mock implementation of API endpoints
export const api = createApi({
  reducerPath: 'api',
  baseQuery: fakeBaseQuery(),
  tagTypes: [
    'User',
    'Department',
    'Post',
    'Tender',
    'Facility',
    'Event',
    'Form',
    'FormSubmission',
    'Talent',
    'Subsite',
    'Complaint',
    'SecurityClearance',
    'Portfolio',
    'Delegation',
    'Settings',
    'Analytics',
    'Media',
  ],
  endpoints: (builder) => ({
    // Departments
    getDepartments: builder.query<Department[], void>({
      async queryFn() {
        return { data: mockDB.departments };
      },
      providesTags: ['Department'],
    }),

    getDepartment: builder.query<Department, string>({
      async queryFn(id) {
        const dept = mockDB.departments.find((d) => d.id === id || d.slug === id);
        return dept ? { data: dept } : { error: { status: 404, data: 'Not found' } };
      },
      providesTags: ['Department'],
    }),

    // Posts
    getPosts: builder.query<
      PaginatedResponse<Post>,
      { page?: number; pageSize?: number; status?: string; departmentId?: string }
    >({
      async queryFn({ page = 1, pageSize = 10, status, departmentId }) {
        let filtered = [...mockDB.posts];
        if (status) filtered = filtered.filter((p) => p.status === status);
        if (departmentId) filtered = filtered.filter((p) => p.departmentId === departmentId);

        // Sort by publishedAt (fallback to createdAt) DESC to show latest first
        filtered.sort((a, b) => {
          const ad = (a.publishedAt || a.createdAt) ?? '';
          const bd = (b.publishedAt || b.createdAt) ?? '';
          return bd.localeCompare(ad);
        });

        const start = (page - 1) * pageSize;
        const end = start + pageSize;
        const paginated = filtered.slice(start, end);

        return {
          data: {
            data: paginated,
            total: filtered.length,
            page,
            pageSize,
            totalPages: Math.ceil(filtered.length / pageSize),
          },
        };
      },
      providesTags: ['Post'],
    }),

    getPost: builder.query<Post, string>({
      async queryFn(id) {
        const post = mockDB.posts.find((p) => p.id === id || p.slug === id);
        return post ? { data: post } : { error: { status: 404, data: 'Not found' } };
      },
      providesTags: ['Post'],
    }),

    createPost: builder.mutation<Post, Partial<Post>>({
      async queryFn(post) {
        const newPost: Post = {
          id: generateId(),
          ...post,
          status: 'draft',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        } as Post;
        mockDB.posts.push(newPost);
        return { data: newPost };
      },
      invalidatesTags: ['Post'],
    }),

    updatePost: builder.mutation<Post, { id: string; data: Partial<Post> }>({
      async queryFn({ id, data }) {
        const index = mockDB.posts.findIndex((p) => p.id === id);
        if (index === -1) return { error: { status: 404, data: 'Not found' } };
        mockDB.posts[index] = {
          ...mockDB.posts[index],
          ...data,
          updatedAt: new Date().toISOString(),
        };
        return { data: mockDB.posts[index] };
      },
      invalidatesTags: ['Post'],
    }),

    submitPost: builder.mutation<Post, string>({
      async queryFn(id) {
        const index = mockDB.posts.findIndex((p) => p.id === id);
        if (index === -1) return { error: { status: 404, data: 'Not found' } };
        mockDB.posts[index].status = 'pending';
        return { data: mockDB.posts[index] };
      },
      invalidatesTags: ['Post'],
    }),

    approvePost: builder.mutation<Post, string>({
      async queryFn(id) {
        const index = mockDB.posts.findIndex((p) => p.id === id);
        if (index === -1) return { error: { status: 404, data: 'Not found' } };
        mockDB.posts[index].status = 'published';
        mockDB.posts[index].publishedAt = new Date().toISOString();
        return { data: mockDB.posts[index] };
      },
      invalidatesTags: ['Post'],
    }),

    rejectPost: builder.mutation<Post, string>({
      async queryFn(id) {
        const index = mockDB.posts.findIndex((p) => p.id === id);
        if (index === -1) return { error: { status: 404, data: 'Not found' } };
        mockDB.posts[index].status = 'rejected';
        return { data: mockDB.posts[index] };
      },
      invalidatesTags: ['Post'],
    }),

    // Tenders
    getTenders: builder.query<Tender[], { status?: string }>({
      async queryFn({ status }) {
        let filtered = [...mockDB.tenders];
        if (status) filtered = filtered.filter((t) => t.status === status);
        return { data: filtered };
      },
      providesTags: ['Tender'],
    }),

    getTender: builder.query<Tender, string>({
      async queryFn(id) {
        const tender = mockDB.tenders.find((t) => t.id === id);
        return tender ? { data: tender } : { error: { status: 404, data: 'Not found' } };
      },
      providesTags: ['Tender'],
    }),

    registerTenderInterest: builder.mutation<
      { success: boolean },
      { tenderId: string; data: Record<string, string> }
    >({
      async queryFn({ tenderId, data }) {
        const index = mockDB.tenders.findIndex((t) => t.id === tenderId);
        if (index === -1) return { error: { status: 404, data: 'Not found' } };
        if (mockDB.tenders[index].registrations !== undefined) {
          mockDB.tenders[index].registrations! += 1;
        }
        // Mock sending confirmation email
        console.log('Tender registration:', data);
        return { data: { success: true } };
      },
      invalidatesTags: ['Tender'],
    }),

    // Facilities
    getFacilities: builder.query<Facility[], { type?: string; province?: string }>({
      async queryFn({ type, province }) {
        let filtered = [...mockDB.facilities];
        if (type) filtered = filtered.filter((f) => f.type === type);
        if (province) filtered = filtered.filter((f) => f.province === province);
        return { data: filtered };
      },
      providesTags: ['Facility'],
    }),

    // Events
    getEvents: builder.query<Event[], { upcoming?: boolean }>({
      async queryFn({ upcoming }) {
        let filtered = [...mockDB.events];
        if (upcoming) {
          const now = new Date();
          filtered = filtered.filter((e) => new Date(e.start) > now);
        }
        return { data: filtered.sort((a, b) => a.start.localeCompare(b.start)) };
      },
      providesTags: ['Event'],
    }),

    // Media (Photos & Videos) - Mock implementation
    getPhotos: builder.query<PaginatedResponse<any>, { category: string; page?: number; pageSize?: number }>({
      async queryFn({ category, page = 1, pageSize = 20 }) {
        // Mock empty response - to be replaced with real API
        return {
          data: {
            data: [],
            total: 0,
            page,
            pageSize,
            totalPages: 0,
          },
        };
      },
      providesTags: ['Media'],
    }),

    getVideos: builder.query<PaginatedResponse<any>, { category: string; page?: number; pageSize?: number }>({
      async queryFn({ category, page = 1, pageSize = 20 }) {
        // Mock empty response - to be replaced with real API
        return {
          data: {
            data: [],
            total: 0,
            page,
            pageSize,
            totalPages: 0,
          },
        };
      },
      providesTags: ['Media'],
    }),

    // Forms
    getForms: builder.query<FormSchema[], void>({
      async queryFn() {
        return { data: mockDB.formSchemas.filter((f) => f.active) };
      },
      providesTags: ['Form'],
    }),

    getForm: builder.query<FormSchema, string>({
      async queryFn(id) {
        const form = mockDB.formSchemas.find((f) => f.id === id);
        return form ? { data: form } : { error: { status: 404, data: 'Not found' } };
      },
      providesTags: ['Form'],
    }),

    submitForm: builder.mutation<FormSubmission, { formId: string; data: Record<string, unknown> }>({
      async queryFn({ formId, data }) {
        const form = mockDB.formSchemas.find((f) => f.id === formId);
        if (!form) return { error: { status: 404, data: 'Form not found' } };

        let score: number | undefined;
        let passed: boolean | undefined;

        // Calculate score for exams
        if (form.isExam) {
          score = 0;
          form.fields.forEach((field) => {
            if (field.score && data[field.id]) {
              // Simple scoring logic - in real app, check correct answers
              score! += field.score;
            }
          });
          passed = form.passingScore ? score >= form.passingScore : undefined;
        }

        const submission: FormSubmission = {
          id: generateId(),
          formId,
          data,
          score,
          passed,
          userMeta: {
            ip: '127.0.0.1',
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
          },
          status: 'received',
          createdAt: new Date().toISOString(),
        };

        mockDB.formSubmissions.push(submission);

        // Send notifications
        await sendAllNotifications(
          form.notifications.emailTo,
          form.notifications.whatsappTo,
          form.notifications.smsTo,
          `New submission for ${form.title.en}`,
          JSON.stringify(data, null, 2)
        );

        return { data: submission };
      },
      invalidatesTags: ['FormSubmission'],
    }),

    getFormSubmissions: builder.query<FormSubmission[], string>({
      async queryFn(formId) {
        return { data: mockDB.formSubmissions.filter((s) => s.formId === formId) };
      },
      providesTags: ['FormSubmission'],
    }),

    // Complaints
    submitComplaint: builder.mutation<Complaint, Omit<Complaint, 'id' | 'caseNumber' | 'status' | 'createdAt' | 'updatedAt'>>({
      async queryFn(complaint) {
        const newComplaint: Complaint = {
          ...complaint,
          id: generateId(),
          caseNumber: generateCaseNumber(),
          status: 'received',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        mockDB.complaints.push(newComplaint);

        // Mock sending notifications
        await sendAllNotifications(
          ['complaints@moys.gov.iq'],
          ['+964770123456'],
          ['+964770123456'],
          `New Complaint: ${complaint.subject}`,
          `Case Number: ${newComplaint.caseNumber}\nFrom: ${complaint.fullName}\nSubject: ${complaint.subject}`
        );

        return { data: newComplaint };
      },
      invalidatesTags: ['Complaint'],
    }),

    getComplaints: builder.query<Complaint[], { status?: string }>({
      async queryFn({ status }) {
        let filtered = [...mockDB.complaints];
        if (status) filtered = filtered.filter((c) => c.status === status);
        return { data: filtered };
      },
      providesTags: ['Complaint'],
    }),

    updateComplaint: builder.mutation<Complaint, { id: string; status: string; notes?: string }>({
      async queryFn({ id, status, notes }) {
        const index = mockDB.complaints.findIndex((c) => c.id === id);
        if (index === -1) return { error: { status: 404, data: 'Not found' } };
        mockDB.complaints[index] = {
          ...mockDB.complaints[index],
          status: status as any,
          notes,
          updatedAt: new Date().toISOString(),
        };
        return { data: mockDB.complaints[index] };
      },
      invalidatesTags: ['Complaint'],
    }),

    // Talents
    getTalents: builder.query<Talent[], { category?: string }>({
      async queryFn({ category }) {
        let filtered = [...mockDB.talents];
        if (category) filtered = filtered.filter((t) => t.category === category);
        return { data: filtered };
      },
      providesTags: ['Talent'],
    }),

    // Subsites
    getSubsites: builder.query<Subsite[], void>({
      async queryFn() {
        return { data: mockDB.subsites.filter((s) => s.active) };
      },
      providesTags: ['Subsite'],
    }),

    // Security Clearances
    submitSecurityClearance: builder.mutation<SecurityClearance, Partial<SecurityClearance>>({
      async queryFn(clearance) {
        const newClearance: SecurityClearance = {
          id: generateId(),
          ...clearance,
          status: 'new',
          requestedDate: new Date().toISOString(),
        } as SecurityClearance;
        mockDB.securityClearances.push(newClearance);
        return { data: newClearance };
      },
      invalidatesTags: ['SecurityClearance'],
    }),

    getSecurityClearances: builder.query<SecurityClearance[], void>({
      async queryFn() {
        return { data: mockDB.securityClearances };
      },
      providesTags: ['SecurityClearance'],
    }),

    // Portfolio
    getPortfolioItems: builder.query<PortfolioItem[], void>({
      async queryFn() {
        return { data: mockDB.portfolioItems };
      },
      providesTags: ['Portfolio'],
    }),

    // Delegations
    getDelegations: builder.query<Delegation[], void>({
      async queryFn() {
        return { data: mockDB.delegations };
      },
      providesTags: ['Delegation'],
    }),

    // Settings
    getSettings: builder.query<SiteSettings, void>({
      async queryFn() {
        return { data: mockDB.siteSettings };
      },
      providesTags: ['Settings'],
    }),

    // Analytics
    getAnalytics: builder.query<AnalyticsData, void>({
      async queryFn() {
        // Generate mock analytics data
        const data: AnalyticsData = {
          pageViews: {
            total: 125000,
            unique: 45000,
            trend: Array.from({ length: 30 }, (_, i) => ({
              date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              views: Math.floor(Math.random() * 5000) + 1000,
            })),
          },
          topPages: [
            { path: '/', views: 25000, title: 'Home' },
            { path: '/tenders', views: 15000, title: 'Tenders' },
            { path: '/events', views: 12000, title: 'Events' },
            { path: '/map', views: 10000, title: 'Map' },
            { path: '/complaints', views: 8000, title: 'Complaints' },
          ],
          submissionsByDept: mockDB.departments.map((d) => ({
            dept: d.nameEn,
            count: Math.floor(Math.random() * 100) + 10,
          })),
          complaintsByStatus: [
            { status: 'received', count: 45 },
            { status: 'in_review', count: 28 },
            { status: 'resolved', count: 120 },
            { status: 'closed', count: 89 },
          ],
          tenderInterest: mockDB.tenders.map((t) => ({
            tender: t.titleEn,
            registrations: t.registrations || 0,
          })),
          deviceBreakdown: [
            { device: 'Desktop', percentage: 45 },
            { device: 'Mobile', percentage: 40 },
            { device: 'Tablet', percentage: 15 },
          ],
        };
        return { data };
      },
      providesTags: ['Analytics'],
    }),
  }),
});

export const {
  // useGetDepartmentsQuery, // Now using real Odoo backend - exported below
  // useGetDepartmentQuery,  // Now using real Odoo backend - exported below
  // useGetPostsQuery, // Now using real Odoo backend - exported below
  // useGetPostQuery,  // Now using real Odoo backend - exported below
  useCreatePostMutation,
  useUpdatePostMutation,
  useSubmitPostMutation,
  useApprovePostMutation,
  useRejectPostMutation,
  // useGetTendersQuery, // Now using real Odoo backend - exported below
  // useGetTenderQuery,  // Now using real Odoo backend - exported below
  useRegisterTenderInterestMutation,
  useGetFacilitiesQuery,
  // useGetEventsQuery, // Now using real Odoo backend - exported below
  useGetFormsQuery,
  useGetFormQuery,
  useSubmitFormMutation,
  useGetFormSubmissionsQuery,
  useSubmitComplaintMutation,
  useGetComplaintsQuery,
  useUpdateComplaintMutation,
  // useGetTalentsQuery, // Now using real Odoo backend - exported below
  useGetSubsitesQuery,
  useSubmitSecurityClearanceMutation,
  useGetSecurityClearancesQuery,
  useGetPortfolioItemsQuery,
  // useGetDelegationsQuery, // Now using real Odoo backend - exported below
  useGetSettingsQuery,
  useGetAnalyticsQuery,
  useGetPhotosQuery,
  useGetVideosQuery,
} = api;

// Re-export hooks from real Odoo backend API
// These are imported from odoo-hooks.ts which handles the initialization order
export {
  // Posts/News
  useGetPostsQuery,
  useGetPostQuery,
  // Talents
  useGetTalentsQuery,
  useGetTalentQuery,
  // Partners
  useGetPartnersQuery,
  useGetPartnerQuery,
  // Departments
  useGetDepartmentsQuery,
  useGetDepartmentQuery,
  // Tenders
  useGetTendersQuery,
  useGetTenderQuery,
  // Delegations
  useGetDelegationsQuery,
  // About Ministry
  useGetAboutQuery,
  // Gulf Cup
  useGetGulfCupQuery,
  // Programs (all types)
  useGetProgramsQuery,
  // Home Slider
  useGetHomeSlidersQuery,
  // Events
  useGetEventsQuery,
  useGetEventQuery,
  // Electronic Forms
  useGetElectronicFormsQuery,
  // Assessments (Tests)
  useGetAssessmentsQuery,
  useGetAssessmentQuery,
  useCreateUserTokenMutation,
} from './api/odoo-hooks';





